import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Rocket, Zap, Gift, Info, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";

export const BoostListingButton = ({ listingId, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadInfo = async () => {
    setInfo(null);
    try {
      const { data } = await api.get(`/listings/${listingId}/boost/info`);
      setInfo(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setOpen(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) loadInfo();
  };

  const buyPaid = async (days) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/boost`, {
        duration_days: days, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const useCredit = async () => {
    setBusy(true);
    try {
      await api.post(`/listings/${listingId}/boost`, { use_credit: true });
      toast.success("Free boost applied — premium feed placement is live.");
      setOpen(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const ambassadorBoost = async (days) => {
    setBusy(true);
    try {
      await api.post(`/listings/${listingId}/boost`, { duration_days: days });
      toast.success("Free ambassador boost applied — premium feed placement is live.");
      setOpen(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const until = info?.boosted_until ? new Date(info.boosted_until).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          data-testid="boost-listing-btn"
          className="w-full flex items-center justify-center gap-2 border border-volt/60 text-volt hover:bg-volt/10 font-mono uppercase text-sm py-2.5 transition-colors"
        >
          <Rocket className="w-4 h-4" /> Boost Listing
        </button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-sniper text-foreground rounded-none" data-testid="boost-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2">
            <Rocket className="w-5 h-5 text-volt" /> Boost this listing
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs font-mono uppercase text-muted-foreground">
          Premium placement near the top of the feed. Just 2 boost slots per page, so spots are limited.
        </DialogDescription>

        {!info ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground" data-testid="boost-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3 mt-1">
            {info.is_boosted && (
              <div className="border border-volt/40 bg-volt/10 px-3 py-2 text-xs font-mono uppercase text-volt flex items-center gap-2" data-testid="boost-active-note">
                <Zap className="w-3.5 h-3.5" /> Boosted until {until} — buying more extends it
              </div>
            )}

            {info.is_ambassador ? (
              <>
                <div className="border border-olive-bright/50 bg-olive/10 px-3 py-2 text-xs font-mono uppercase text-olive-bright flex items-center gap-2" data-testid="boost-ambassador-note">
                  <Gift className="w-3.5 h-3.5" /> Ambassador — all boosts are free & unlimited
                </div>
                <button
                  onClick={() => ambassadorBoost(7)}
                  disabled={busy}
                  data-testid="boost-ambassador-7d"
                  className="w-full flex items-center justify-between gap-2 border border-olive-bright bg-olive/10 hover:bg-olive/20 disabled:opacity-50 px-4 py-3 transition-colors"
                >
                  <span className="font-heading uppercase text-olive-bright">7-day boost</span>
                  <span className="font-heading text-xl text-olive-bright">Free</span>
                </button>
                <button
                  onClick={() => ambassadorBoost(14)}
                  disabled={busy}
                  data-testid="boost-ambassador-14d"
                  className="w-full flex items-center justify-between gap-2 border border-olive-bright bg-olive/10 hover:bg-olive/20 disabled:opacity-50 px-4 py-3 transition-colors"
                >
                  <span className="font-heading uppercase text-olive-bright">14-day boost</span>
                  <span className="font-heading text-xl text-olive-bright">Free</span>
                </button>
              </>
            ) : (
              <>
                {info.credits_remaining > 0 && (
                  <button
                    onClick={useCredit}
                    disabled={busy}
                    data-testid="boost-use-credit"
                    className="w-full flex items-center justify-between gap-2 border border-olive-bright bg-olive/10 hover:bg-olive/20 disabled:opacity-50 px-4 py-3 transition-colors"
                  >
                    <span className="flex items-center gap-2 font-heading uppercase text-olive-bright">
                      <Gift className="w-4 h-4" /> Use free boost
                    </span>
                    <span className="font-mono text-[11px] uppercase text-secondary-foreground">
                      {info.credit_days}-day · {info.credits_remaining} left this month
                    </span>
                  </button>
                )}

                <button
                  onClick={() => buyPaid(7)}
                  disabled={busy}
                  data-testid="boost-buy-7d"
                  className="w-full flex items-center justify-between gap-2 border border-sniper hover:border-volt disabled:opacity-50 px-4 py-3 transition-colors"
                >
                  <span className="font-heading uppercase text-foreground">7-day boost</span>
                  <span className="font-heading text-xl text-volt">${Number(info.fee_7d).toFixed(2)}</span>
                </button>

                <button
                  onClick={() => buyPaid(14)}
                  disabled={busy}
                  data-testid="boost-buy-14d"
                  className="w-full flex items-center justify-between gap-2 border border-sniper hover:border-volt disabled:opacity-50 px-4 py-3 transition-colors"
                >
                  <span className="font-heading uppercase text-foreground">14-day boost</span>
                  <span className="font-heading text-xl text-volt">${Number(info.fee_14d).toFixed(2)}</span>
                </button>

                <p className="flex items-start gap-1.5 text-[11px] font-mono text-muted-foreground leading-snug pt-1">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-olive-bright" />
                  Paid boosts checkout securely via Stripe. Placement is a weighted bump, not a permanent pin — real results vary.
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
